import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  EditButton,
  DeleteButton,
  SearchInput,
  FilterButton,
  TopToolbar,
  CreateButton,
  SelectInput,
  BooleanInput,
  useTranslate,
  FunctionField,
  useListContext,
  RecordContextProvider,
  RefreshButton,
  ExportButton,
} from 'react-admin';
import { Box, Chip, Typography, useMediaQuery, useTheme, Fab } from '@mui/material';
import {
  Storage as StorageIcon,
  CheckCircle,
  Cancel,
  CloudQueue,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SupabaseConnectionFilters = (translate: any) => [
  <SearchInput source="search" alwaysOn key="search" />,
  <SelectInput
    key="environment"
    source="environment"
    choices={[
      { id: 'production', name: 'Production' },
      { id: 'staging', name: 'Staging' },
      { id: 'development', name: 'Development' },
    ]}
  />,
  <BooleanInput
    key="isActive"
    source="isActive"
    label={translate('resources.supabase-connections.list.filters.activeOnly')}
  />,
];

const DesktopListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
  </TopToolbar>
);

const MobileListActions = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
    <RefreshButton />
    <ExportButton />
  </Box>
);

const PageHeader = () => {
  const translate = useTranslate();

  return (
    <Box sx={{ mb: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CloudQueue sx={{ fontSize: { xs: 22, sm: 28 }, color: '#00C853' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {translate('resources.supabase-connections.name')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {translate('resources.supabase-connections.list.subtitle')}
      </Typography>
    </Box>
  );
};

const getEnvironmentChip = (environment: string) => (
  <Chip
    label={environment}
    size="small"
    sx={{
      backgroundColor:
        environment === 'production'
          ? '#FFEBEE'
          : environment === 'staging'
          ? '#FFF3E0'
          : '#E3F2FD',
      color:
        environment === 'production'
          ? '#C62828'
          : environment === 'staging'
          ? '#F57C00'
          : '#1565C0',
      fontWeight: 500,
    }}
  />
);

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const translate = useTranslate();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/supabase-connections/${record.id}/show`)}
            sx={{
              p: 2,
              backgroundColor: '#FFFFFF',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              '&:active': { backgroundColor: '#F5F5F5' },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.name}
              </Typography>
              {getEnvironmentChip(record.environment)}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {record.is_active ? (
                  <>
                    <CheckCircle sx={{ fontSize: 16, color: '#00C853' }} />
                    <Typography variant="caption" sx={{ color: '#00C853' }}>
                      {translate('resources.supabase-connections.statuses.active')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Cancel sx={{ fontSize: 16, color: '#9E9E9E' }} />
                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                      {translate('resources.supabase-connections.statuses.inactive')}
                    </Typography>
                  </>
                )}
              </Box>
              {record.encrypted_service_key && (
                <Chip
                  size="small"
                  label="Service Key"
                  sx={{ backgroundColor: '#E8F5E8', color: '#1B5E20', fontSize: '0.7rem', height: 22 }}
                />
              )}
              {record.database_url_encrypted && (
                <Chip
                  size="small"
                  label="DB URL"
                  sx={{ backgroundColor: '#FFF3E0', color: '#E65100', fontSize: '0.7rem', height: 22 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {record.supabase_url}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {record.created_at ? new Date(record.created_at).toLocaleDateString() : ''}
              </Typography>
            </Box>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

export const SupabaseConnectionsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <Box sx={{ position: 'relative' }}>
      <PageHeader />
      <List
        filters={SupabaseConnectionFilters(translate)}
        actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
        sort={{ field: 'created_at', order: 'DESC' }}
        perPage={25}
        empty={isMobile ? false : undefined}
        sx={{
          '& .RaList-content': {
            backgroundColor: isMobile ? 'transparent' : '#FFFFFF',
            borderRadius: 2,
            boxShadow: isMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
          },
          ...(isMobile && {
            '& .RaList-content': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
              mt: 2,
            },
            '& .RaList-actions': {
              backgroundColor: 'transparent !important',
              p: '0 !important',
              minHeight: 'auto !important',
            },
            '& .RaListToolbar-root': {
              backgroundColor: 'transparent !important',
              flexDirection: 'row !important',
              flexWrap: 'wrap !important',
              alignItems: 'center !important',
              gap: '8px !important',
            },
            '& .RaListToolbar-root > span': {
              display: 'none',
            },
            '& .RaFilterButton-root, & .add-filter': {
              display: 'none !important',
            },
            '& .RaListToolbar-root > form, & .RaFilterForm-root': {
              flex: '1 1 100% !important',
              width: '100% !important',
            },
            '& .filter-field, & .RaFilterForm-filterFormInput': {
              width: '100% !important',
            },
            '& .filter-field .MuiFormControl-root': {
              width: '100% !important',
            },
            '& .filter-field .MuiInputBase-root': {
              width: '100% !important',
            },
            '& .MuiToolbar-root': {
              backgroundColor: 'transparent !important',
              minHeight: 'auto !important',
            },
          }),
        }}
      >
        {isMobile ? (
          <MobileList />
        ) : (
          <Datagrid
            rowClick="show"
            sx={{
              '& .RaDatagrid-headerCell': {
                backgroundColor: '#FAFAFA',
                fontWeight: 600,
              },
              '& .RaDatagrid-row': {
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              },
            }}
          >
            <TextField source="name" label={translate('resources.supabase-connections.fields.name')} />

            <FunctionField
              label={translate('resources.supabase-connections.fields.environment')}
              render={(record: any) => getEnvironmentChip(record.environment)}
            />

            <TextField
              source="supabase_url"
              label={translate('resources.supabase-connections.fields.supabaseUrl')}
              sx={{
                '& .MuiTypography-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />

            <FunctionField
              label={translate('resources.supabase-connections.fields.status')}
              render={(record: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {record.is_active ? (
                    <>
                      <CheckCircle sx={{ fontSize: 18, color: '#00C853' }} />
                      <Typography variant="body2" sx={{ color: '#00C853' }}>
                        {translate('resources.supabase-connections.statuses.active')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Cancel sx={{ fontSize: 18, color: '#9E9E9E' }} />
                      <Typography variant="body2" sx={{ color: '#9E9E9E' }}>
                        {translate('resources.supabase-connections.statuses.inactive')}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            />

            <FunctionField
              label={translate('resources.supabase-connections.fields.credentials')}
              render={(record: any) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {record.encrypted_service_key && (
                    <Chip
                      size="small"
                      label="Service Key"
                      sx={{
                        backgroundColor: '#E8F5E8',
                        color: '#1B5E20',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  {record.database_url_encrypted && (
                    <Chip
                      size="small"
                      label="DB URL"
                      sx={{
                        backgroundColor: '#FFF3E0',
                        color: '#E65100',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                </Box>
              )}
            />

            <DateField
              source="created_at"
              label={translate('resources.supabase-connections.fields.createdAt')}
              showTime
            />

            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>

      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => navigate('/supabase-connections/create')}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};
